import React, { useState, useMemo, useEffect, useContext } from 'react';
import { Row, Col } from 'react-bootstrap';
import {
  FileText, Eye, Tag, Plus, Search,
  Pencil, Trash2, X, ToggleLeft, ToggleRight,
} from 'lucide-react';
import '../../../assets/styles/admin/AdminBlogManagement.css';
import AdminMasterLayout from '../../../layouts/admin/AdminMasterLayout ';
import axios from 'axios';
import Swal from 'sweetalert2';
import AppContext from '../../../context/AppContext';

/* ══════════════════════════════════════════════
   HELPER — Swal alert
══════════════════════════════════════════════ */
const showAlert = (success, message) =>
  Swal.fire({
    icon:              success ? 'success' : 'error',
    title:             success ? 'Done!'   : 'Oops!',
    text:              message,
    timer:             2000,
    showConfirmButton: false,
  });

/* ══════════════════════════════════════════════
   CREATE / EDIT MODAL
══════════════════════════════════════════════ */
const BlogFormModal = ({ post, onClose, onSave }) => {
  const isEdit = !!post;
  const [form, setForm] = useState({
    title:      post?.title                   || '',
    slug:       post?.slug                    || '',
    coverImage: post?.coverImage              || '',
    excerpt:    post?.excerpt                 || '',
    content:    post?.content                 || '',
    tags:       post?.tags                    || '',   // already a string from API
    author:     post?.author                  || 'Admin',
    published:  post?.status === 'Published'  || false,
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleTitleChange = v => {
    set('title', v);
    if (!isEdit) set('slug', v.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
  };

  const handleSave = () => {
    if (!form.title.trim() || !form.excerpt.trim() || !form.content.trim()) {
      return showAlert(false, 'Please fill all required fields');
    }
    setLoading(true);
    onSave({
      id:         post?.id,
      title:      form.title,
      slug:       form.slug || form.title.toLowerCase().replace(/\s+/g, '-'),
      coverImage: form.coverImage,
      excerpt:    form.excerpt,
      content:    form.content,
      tags:       form.tags,        // send as string
      author:     form.author,
      status:     form.published ? 'Published' : 'Draft',
    }, () => setLoading(false));
  };

  return (
    <div className="ablog-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ablog-modal ablog-modal--form ablog-modal-anim">
        <button className="ablog-modal__x" onClick={onClose}><X size={18} /></button>
        <h3 className="ablog-modal__title">{isEdit ? 'Edit Blog Post' : 'Create Blog Post'}</h3>
        <p className="ablog-modal__sub">Fill in the blog post details below</p>

        <div className="ablog-form-scroll">
          <div className="ablog-form-group">
            <label className="ablog-form-label">Title <span className="ablog-required">*</span></label>
            <input
              className="ablog-input ablog-input--focus"
              placeholder="Blog post title"
              value={form.title}
              onChange={e => handleTitleChange(e.target.value)}
            />
          </div>

          <div className="ablog-form-group">
            <label className="ablog-form-label">Slug</label>
            <input
              className="ablog-input"
              placeholder="auto-generated-from-title"
              value={form.slug}
              onChange={e => set('slug', e.target.value)}
            />
            <p className="ablog-form-hint">Leave empty to auto-generate from title</p>
          </div>

          <div className="ablog-form-group">
            <label className="ablog-form-label">Cover Image URL</label>
            <input
              className="ablog-input"
              placeholder="https://example.com/image.jpg"
              value={form.coverImage}
              onChange={e => set('coverImage', e.target.value)}
            />
          </div>

          <div className="ablog-form-group">
            <label className="ablog-form-label">Excerpt <span className="ablog-required">*</span></label>
            <textarea
              className="ablog-textarea ablog-textarea--sm"
              placeholder="Short summary of the blog post..."
              value={form.excerpt}
              onChange={e => set('excerpt', e.target.value)}
            />
          </div>

          <div className="ablog-form-group">
            <label className="ablog-form-label">Content <span className="ablog-required">*</span></label>
            <textarea
              className="ablog-textarea ablog-textarea--lg"
              placeholder="Full blog post content..."
              value={form.content}
              onChange={e => set('content', e.target.value)}
            />
          </div>

          <Row className="g-3">
            <Col xs={12} md={6}>
              <div className="ablog-form-group">
                <label className="ablog-form-label">Tags (comma separated)</label>
                <input
                  className="ablog-input"
                  placeholder="dairy, farming, milk"
                  value={form.tags}
                  onChange={e => set('tags', e.target.value)}
                />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="ablog-form-group">
                <label className="ablog-form-label">Author Name</label>
                <input
                  className="ablog-input"
                  placeholder="Admin"
                  value={form.author}
                  onChange={e => set('author', e.target.value)}
                />
              </div>
            </Col>
          </Row>

          <div className="ablog-form-group ablog-toggle-row">
            <button
              className="ablog-toggle"
              onClick={() => set('published', !form.published)}
              type="button"
            >
              {form.published
                ? <ToggleRight size={36} color="var(--secondary-color)" />
                : <ToggleLeft  size={36} color="#9ca3af" />
              }
            </button>
            <span className="ablog-toggle__label">Publish immediately</span>
          </div>
        </div>

        <div className="ablog-modal__footer">
          <button className="ablog-modal__cancel" onClick={onClose}>Cancel</button>
          <button className="ablog-modal__submit" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════
   VIEW MODAL
══════════════════════════════════════════════ */
const ViewModal = ({ post, onClose }) => {
  // tags string ko array mein convert karo display ke liye
  const tagsArr = post.tags ? post.tags.split(',').map(t => t.trim()).filter(Boolean) : [];

  return (
    <div className="ablog-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ablog-modal ablog-modal--view ablog-modal-anim">
        <button className="ablog-modal__x" onClick={onClose}><X size={18} /></button>
        <h3 className="ablog-view__title">{post.title}</h3>
        <p className="ablog-view__byline">By {post.author} · {new Date(post.created_at).toLocaleDateString()}</p>
        <div className="ablog-view__tags">
          {tagsArr.map(t => <span key={t} className="ablog-tag">{t}</span>)}
        </div>
        <p className="ablog-view__excerpt">{post.excerpt}</p>
        <p className="ablog-view__content">{post.content}</p>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════
   DELETE MODAL
══════════════════════════════════════════════ */
const DeleteModal = ({ post, onClose, onConfirm }) => (
  <div className="ablog-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
    <div className="ablog-modal ablog-modal--sm ablog-modal-anim">
      <button className="ablog-modal__x" onClick={onClose}><X size={18} /></button>
      <h3 className="ablog-modal__title">Delete Blog Post</h3>
      <p className="ablog-modal__sub ablog-modal__sub--dark">
        Are you sure you want to delete <strong>"{post.title}"</strong>? This action cannot be undone.
      </p>
      <div className="ablog-modal__footer">
        <button className="ablog-modal__cancel" onClick={onClose}>Cancel</button>
        <button className="ablog-modal__delete" onClick={() => { onConfirm(post.id); onClose(); }}>Delete</button>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
const AdminBlogManagement = () => {
  const { baseUrl }               = useContext(AppContext);
  const [blogs,      setBlogs]    = useState([]);
  
  const [search,     setSearch]   = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editPost,   setEditPost]   = useState(null);
  const [viewPost,   setViewPost]   = useState(null);
  const [deletePost, setDeletePost] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  /* ── Load all blogs on mount ── */
  const viewAllBlogs = () => {
    setPageLoading(true);
    axios
      .get(`${baseUrl}/admin/blog/viewAllBlogs`)
      .then(res => {
        console.log('viewAllBlogs:', res.data);
        if (res.data.success) setBlogs(res.data.blogs);
      })
      .catch(err => console.log('fetchBlogs error:', err.message))
      .finally(() => setPageLoading(false));
  };

  useEffect(() => { viewAllBlogs(); }, []);

  /* ── Stats ── */
  const totalPosts = blogs.length;
  const published  = blogs.filter(p => p.status === 'Published').length;
  const drafts     = blogs.filter(p => p.status === 'Draft').length;

  /* ── Search filter ── */
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return blogs;
    return blogs.filter(p =>
      p.title.toLowerCase().includes(q) ||
      (p.tags || '').toLowerCase().includes(q)
    );
  }, [blogs, search]);

  /* ── Tags string → array for display ── */
  const parseTags = (tagsStr) =>
    tagsStr ? tagsStr.split(',').map(t => t.trim()).filter(Boolean) : [];

  /* ── CREATE ── */
  const addBlog = (data, done) => {
    const formData = new FormData();
    // Object.entries(data).forEach(([k, v]) => formData.append(k, v ?? ''));
    formData.append("")

    axios
      .post(`${baseUrl}/admin/blog/addBlog`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(res => {
        console.log('addBlog:', res.data);
        showAlert(res.data.success, res.data.message);
        if (res.data.success) { viewAllBlogs(); setCreateOpen(false); }
      })
      .catch(() => showAlert(false, 'Something went wrong'))
      .finally(done);
  };

  /* ── UPDATE ── */
  const updateBlog = (data, done) => {
    const formData = new FormData();
    formData.append('blog_id', data.id);
    Object.entries(data).forEach(([k, v]) => { if (k !== 'id') formData.append(k, v ?? ''); });

    axios
      .post(`${baseUrl}/admin/blog/updateBlog`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(res => {
        console.log('updateBlog:', res.data);
        showAlert(res.data.success, res.data.message);
        if (res.data.success) { viewAllBlogs(); setEditPost(null); }
      })
      .catch(() => showAlert(false, 'Something went wrong'))
      .finally(done);
  };

  /* ── DELETE ── */
  const deleteBlog = (id) => {
    const formData = new FormData();
    formData.append('blog_id', id);

    axios
      .post(`${baseUrl}/admin/blog/deleteBlog`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(res => {
        console.log('deleteBlog:', res.data);
        showAlert(res.data.success, res.data.message);
        if (res.data.success) viewAllBlogs();
      })
      .catch(() => showAlert(false, 'Something went wrong'));
  };

  /* ── TOGGLE STATUS ── */
  const handleToggleStatus = (blog) => {
    const formData = new FormData();
    formData.append('blog_id', blog.id);

    axios
      .post(`${baseUrl}/admin/blog/toggleStatus`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(res => {
        console.log('toggleStatus:', res.data);
        if (res.data.success) viewAllBlogs();
      })
      .catch(err => console.log('toggleStatus error:', err.message));
  };

  const stats = [
    { label: 'Total Posts', value: totalPosts, Icon: FileText, color: 'var(--secondary-color)', bg: 'rgba(62,150,231,0.08)' },
    { label: 'Published',   value: published,  Icon: Eye,      color: '#22c55e',                bg: 'rgba(34,197,94,0.08)'  },
    { label: 'Drafts',      value: drafts,     Icon: Tag,      color: '#f59e0b',                bg: 'rgba(245,158,11,0.08)' },
  ];

  return (
    <AdminMasterLayout>
      <div className="ablog-wrapper">

        {/* ── Header ── */}
        <div className="ablog-header">
          <div>
            <h1 className="ablog-title">Blog Management</h1>
            <p className="ablog-subtitle">Create and manage blog posts</p>
          </div>
          <button className="ablog-create-btn" onClick={() => setCreateOpen(true)}>
            <Plus size={16} /> Create Blog Post
          </button>
        </div>

        {/* ── Stats ── */}
        <Row className="g-3 mb-4">
          {stats.map(({ label, value, Icon, color, bg }, i) => (
            <Col xs={12} md={4} key={i}>
              <div className="ablog-stat-card" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="ablog-stat-card__icon" style={{ color, background: bg }}>
                  <Icon size={22} strokeWidth={1.8} />
                </div>
                <div>
                  <p className="ablog-stat-card__val" style={{ color }}>{value}</p>
                  <p className="ablog-stat-card__lbl">{label}</p>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* ── Table Card ── */}
        <div className="ablog-card ablog-anim" style={{ animationDelay: '0.22s' }}>

          <div className="ablog-search-wrap">
            <Search size={15} className="ablog-search__icon" />
            <input
              className="ablog-search__input"
              placeholder="Search by title or tag..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div className="ablog-table-wrap">
            <table className="ablog-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Tags</th>
                  <th>Author</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th className="ablog-th--right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageLoading ? (
                  <tr>
                    <td colSpan={6} className="ablog-table__empty">Loading...</td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="ablog-table__empty">No blog posts found.</td>
                  </tr>
                ) : filtered.map(post => {
                  const tagsArr = parseTags(post.tags);
                  return (
                    <tr key={post.id} className="ablog-table__row">
                      <td className="ablog-td-title">{post.title}</td>
                      <td>
                        <div className="ablog-tags-cell">
                          {tagsArr.slice(0, 2).map(t => (
                            <span key={t} className="ablog-tag">{t}</span>
                          ))}
                          {tagsArr.length > 2 && (
                            <span className="ablog-tag ablog-tag--more">+{tagsArr.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td>{post.author}</td>
                      <td>
                        {/* Click on badge to toggle status */}
                        <span
                          className={`ablog-status-badge ablog-status-badge--${post.status.toLowerCase()} ablog-status-badge--clickable`}
                          onClick={() => handleToggleStatus(post)}
                          title="Click to toggle status"
                        >
                          {post.status}
                        </span>
                      </td>
                      <td className="ablog-td-date">{post.created_at ? new Date(post.created_at).toLocaleDateString() : 'N/A'} </td>
                      <td>
                        <div className="ablog-actions">
                          <button
                            className="ablog-action-btn ablog-action-btn--view"
                            onClick={() => setViewPost(post)}
                            title="View"
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            className="ablog-action-btn ablog-action-btn--edit"
                            onClick={() => setEditPost(post)}
                            title="Edit"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            className="ablog-action-btn ablog-action-btn--delete"
                            onClick={() => setDeletePost(post)}
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Modals ── */}
        {createOpen && (
          <BlogFormModal
            post={null}
            onClose={() => setCreateOpen(false)}
            onSave={addBlog}
          />
        )}
        {editPost && (
          <BlogFormModal
            post={editPost}
            onClose={() => setEditPost(null)}
            onSave={updateBlog}
          />
        )}
        {viewPost   && <ViewModal   post={viewPost}   onClose={() => setViewPost(null)} />}
        {deletePost && <DeleteModal post={deletePost} onClose={() => setDeletePost(null)} onConfirm={deleteBlog} />}
      </div>
    </AdminMasterLayout>
  );
};

export default AdminBlogManagement;